import type { ToolDefinition, ToolType } from "../types";

const STICKER_MAKER_TOOL_TYPE = { label: "Sticker Studio", icon: "Zap" } as const satisfies ToolType;

const stickerMakerTool: ToolDefinition = {
    slug: "sticker-maker",
    toolType: STICKER_MAKER_TOOL_TYPE,
    heroBadges: [
      "100% Free",
      "Static & Animated",
      "WhatsApp Ready",
      "Telegram Ready",
      "Direct Sharing",
      "No Account Required"
    ],
    name: "Sticker Maker Online | Create WhatsApp & Telegram Stickers from Images or Videos — 512×512 WebP",
    description: "Create 512x512 WebP stickers for WhatsApp and Telegram from images or videos up to 6 seconds. FPS, quality presets, and reverse playback included. Free.",
    longDescription: "Free online sticker maker generating 512×512 WebP stickers — the exact specification for WhatsApp and Telegram. Image stickers: transparent RGBA canvas, WebP quality 80. Animated video stickers: FFmpeg-encoded animated WebP with up to 6 seconds, FPS slider (1–20), four quality presets with adaptive fallback encoding to stay under WhatsApp's 512KB limit, and reverse toggle for boomerang loops. Direct 'Share to WhatsApp' button on mobile via Web Share API. Free, no watermark.",
    category: "Social Media Tools",
    categorySlug: "social-media-tools",
    href: "/tools/sticker-maker",
    api: "/sticker-maker",
    keywords: [
        "sticker maker",
        "whatsapp pack creator",
        "telegram pack tool",
        "image to sticker converter",
        "video to webp converter",
        "snappy fix sticker maker",
        "animated pack creator",
        "messaging cutout tool",
        "photo cutout maker",
        "custom pack builder",
        "webp emoji tool",
        "chat asset creator"
      ],
    pageContent: {
      sections: {
        steps: {
          title: "How to Make a Custom Sticker Online",
          description: "Four steps. Image or video. WhatsApp-ready in seconds."
        },
        benefits: {
          title: "Why Use the Snappy-Fix Sticker Maker?",
          description: "Images and video · adaptive quality · 512×512 transparent output · direct WhatsApp share."
        },
        faqs: {
          description: "Everything about creating WhatsApp stickers, animated stickers, and Studio controls."
        }
      },
      intro: {
        title: "Create Stickers from Images or Videos with One Shared Workflow",
        paragraphs: [
          "This sticker maker supports both static image stickers and animated video stickers, but the output always follows the same goal: a clean 512 × 512 WebP file that works in WhatsApp and Telegram without extra editing.",
          "Upload an image and the tool creates a centred transparent sticker automatically. Upload a short video and the Studio controls let you trim the clip, change FPS, choose a quality preset, and reverse the animation before the encoder targets a messaging-friendly file size.",
        ],
      },
      callouts: [
        {
          title: "Who creates custom stickers",
          items: [
            { label: "Friends making personalised sticker packs for group chats", icon: "MessageCircle", accent: "primary" },
            { label: "Content creators building branded reaction stickers", icon: "ImageIcon", accent: "primary" },
            { label: "Meme makers turning viral moments into reusable stickers", icon: "Zap", accent: "primary" },
            { label: "Businesses adding branded stickers to customer conversations", icon: "TrendingUp", accent: "primary" },
            { label: "Communities building packs around shared interests", icon: "Share2", accent: "primary" },
            { label: "Anyone needing a reaction no default sticker captures", icon: "Sparkles", accent: "primary" },
          ],
          layout: "list",
        },
        {
          title: "Best content for animated stickers",
          items: [
            { label: "Short reaction clips like laughs, nods, and facepalms", icon: "Film", accent: "accent" },
            { label: "Looping motions that feel natural when repeated", icon: "RefreshCw", accent: "accent" },
            { label: "Boomerang-style moments that work well with reverse", icon: "Repeat2", accent: "accent" },
            { label: "Pet clips and expressive character reactions", icon: "Clapperboard", accent: "accent" },
            { label: "Simple motions like waving, pointing, or quick gestures", icon: "Move", accent: "accent" },
            { label: "Meme clips trimmed down to a strong 2–3 second moment", icon: "ScanLine", accent: "accent" },
          ],
          layout: "list",
        },
      ],
      referenceTables: [
        {
          title: "Image sticker vs animated sticker pipeline",
          columns: ["Property", "Image sticker", "Animated sticker"],
          rows: [
            { Property: "Input", "Image sticker": "JPG, PNG, WebP, and other image formats", "Animated sticker": "MP4, WEBM, and other video formats" },
            { Property: "Output format", "Image sticker": "Static WebP with transparent background", "Animated sticker": "Looping animated WebP with transparency" },
            { Property: "Size", "Image sticker": "512 × 512 px centred on canvas", "Animated sticker": "Fits within 512 × 512 px with transparent padding" },
            { Property: "Controls", "Image sticker": "Smart automatic mode", "Animated sticker": "FPS, trim range, quality preset, and reverse" },
            { Property: "Max duration", "Image sticker": "Not applicable", "Animated sticker": "6 seconds maximum" },
            { Property: "File size target", "Image sticker": "Quality 80 WebP, usually around 20–80KB", "Animated sticker": "Adaptive encoding aimed under 512KB" },
            { Property: "WhatsApp compatible", "Image sticker": "Yes", "Animated sticker": "Yes" },
          ],
        },
        {
          title: "Video quality presets",
          columns: ["Preset", "FPS", "Scale", "Best for"],
          rows: [
            { Preset: "Ultra HD", FPS: "15 FPS", Scale: "512px", "Best for": "Maximum quality when file size is not a concern" },
            { Preset: "High", FPS: "12 FPS", Scale: "512px", "Best for": "Near-full quality for most polished stickers" },
            { Preset: "Balanced", FPS: "10 FPS", Scale: "480px", "Best for": "Best quality-to-size ratio for WhatsApp" },
            { Preset: "Small", FPS: "8 FPS", Scale: "420px", "Best for": "Smallest output for longer or busier clips" },
          ],
          emphasisColumns: ["Preset"],
          monospaceColumns: ["FPS", "Scale"],
        },
      ],
      faqs: [
  {
    question:
      "What is the difference between an image sticker and an animated sticker?",
    answer:
      "An image sticker is a static WebP file created from a photo — displayed as a fixed image in messaging apps at 512×512 pixels on a transparent background. An animated sticker is created from a video clip and saved as an animated WebP — it plays as a looping animation in WhatsApp and Telegram. The tool automatically detects whether you uploaded an image or video and applies the appropriate pipeline.",
  },
  {
    question: "What video length and format works best for animated stickers?",
    answer:
      "The tool clips videos to a maximum of 6 seconds. For best results, use a short clip of 2–4 seconds — stickers loop most naturally at this length. MP4 and WEBM are the most reliable formats. The FPS slider (1–20) controls animation smoothness: 10–12 FPS produces smooth-looking stickers at smaller file sizes. The adaptive encoder automatically tries multiple FPS and quality combinations to keep the output under WhatsApp's 512KB sticker limit.",
  },
  {
    question: "What does the Reverse toggle do?",
    answer:
      "The Reverse toggle applies FFmpeg's reverse filter before converting to sticker — the animation plays backwards. This creates a boomerang-style effect where the action plays in one direction and then loops back. It works particularly well for reaction stickers — a hand clap, jump, or any quick motion looks natural both forwards and reversed.",
  },
  {
    question: "What are the quality presets and which should I choose?",
    answer:
      "Ultra HD (HD) targets 15 FPS at 512px scale — maximum quality, largest file. High targets 12 FPS at 512px scale — near-full quality. Balanced (Medium, the default) targets 10 FPS at 480px scale — best size-to-quality ratio for most stickers. Small (Low) targets 8 FPS at 420px scale — smallest output. For all presets, the tool automatically falls back to lower settings if the output exceeds WhatsApp's 512KB limit.",
  },
  {
    question: "What size does the tool output and why 512×512?",
    answer:
      "The tool outputs stickers at 512×512 pixels — the exact size required by WhatsApp and recommended by Telegram for sticker packs. For images, the original is thumbnailed to fit within 512×512 and centred on a transparent canvas. For videos, FFmpeg scales and pads to fit within 512×512 with transparent padding. This ensures the sticker displays correctly in all messaging apps without cropping or distortion.",
  },
  {
    question: "Can I share the sticker directly to WhatsApp from the tool?",
    answer:
      "Yes — on mobile browsers that support the Web Share API. After generating a sticker, the 'Share to WhatsApp' button triggers the native share sheet on your device. The 'Send to App' button opens the general share sheet for other apps. On desktop browsers, use the Download button and add the sticker to WhatsApp via Sticker Studio or your device's file manager.",
  },
  {
    question: "What format does the sticker download in?",
    answer:
      "All stickers download as WebP files — the format natively supported by WhatsApp and Telegram. Image stickers: WebP at quality 80 with transparent background. Animated stickers: animated WebP with loop=0 (infinite loop), encoded to stay under the 512KB WhatsApp sticker limit.",
  },
  {
    question: "Is the Sticker Maker free and does it add a watermark?",
    answer:
      "Yes — completely free with no usage limits and no watermark on generated stickers. Create as many image and animated stickers as you need at no cost with no account required.",
  },
],
      benefits: [
  {
    icon: "ImageIcon",
    title: "Image and video — both supported",
    description:
      "Upload a photo for a static transparent sticker, or a video clip for an animated looping WebP sticker. One tool, two fully-optimised conversion pipelines.",
  },
  {
    icon: "Layers",
    title: "Adaptive quality encoding",
    description:
      "For videos, the encoder automatically tries multiple FPS and quality combinations to produce the best-looking sticker under WhatsApp's 512KB limit.",
  },
  {
    icon: "RefreshCw",
    title: "Reverse animation",
    description:
      "Toggle reverse to flip the video animation backwards — creates a boomerang-style sticker from any short clip. Great for reactions and quick motions.",
  },
  {
    icon: "Zap",
    title: "512×512 transparent output",
    description:
      "All stickers output at the exact 512×512 pixel size required by WhatsApp and Telegram, centred on a transparent background.",
  },
  {
    icon: "Share2",
    title: "Direct WhatsApp share",
    description:
      "On mobile, use the native share buttons to send the sticker directly to WhatsApp or any messaging app via the Web Share API — no download needed.",
  },
  {
    icon: "Globe",
    title: "Works on any device",
    description:
      "Fully responsive Studio interface — create stickers from desktop, laptop, tablet, or smartphone from any modern browser.",
  },
],
      steps: [
  {
    number: "01",
    title: "Upload your image or video",
    description:
      "Drag and drop onto the left panel or click to browse. The tool auto-detects image or video and configures the Studio panel accordingly. For videos, keep clips to 2–4 seconds for best looping stickers.",
  },
  {
    number: "02",
    title: "Configure Studio settings (video only)",
    description:
      "Set Start Time and End Time to trim your clip. Adjust FPS for animation smoothness and choose a quality preset. Toggle Reverse for a boomerang effect. Image stickers use Smart Engine automatically — no settings needed.",
  },
  {
    number: "03",
    title: "Click Generate Sticker",
    description:
      "Hit Generate Sticker and watch the progress bar fill. Video stickers may take a few seconds as the adaptive encoder tries multiple quality settings to meet the 512KB WhatsApp limit.",
  },
  {
    number: "04",
    title: "Download or share directly",
    description:
      "The result panel shows a WhatsApp-style preview. Download the WebP file, use Send to App for the Web Share API, or tap Share to WhatsApp for a direct send on mobile.",
  },
],
    },
  };

export default stickerMakerTool;
export { stickerMakerTool };
